import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Loader, CheckCircle } from 'lucide-react';
import axios from 'axios';

function LocationAutocomplete({ value, onChange, onLocationSelect, required = false }) {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [error, setError] = useState('');
  const wrapperRef = useRef(null);
  const debounceTimer = useRef(null);

  // Fermer les suggestions en cliquant à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fonction de recherche avec debounce
  const searchLocation = async (searchQuery) => {
    if (searchQuery.length < 3) {
      setSuggestions([]);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.get('https://nominatim.openstreetmap.org/search', {
        params: {
          q: searchQuery,
          format: 'json',
          limit: 5,
          addressdetails: 1
        },
        headers: {
          'User-Agent': 'RunClubApp/1.0'
        }
      });

      if (response.data && response.data.length > 0) {
        setSuggestions(response.data);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setError('Aucune ville trouvée. Veuillez vérifier l\'orthographe.');
      }
    } catch (err) {
      console.error('Erreur recherche:', err);
      setError('Erreur lors de la recherche');
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  // Gérer les changements dans l'input
  const handleInputChange = (e) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    setSelectedLocation(null);
    setError('');

    // Annuler le timer précédent
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Démarrer un nouveau timer (attendre 500ms après la dernière frappe)
    debounceTimer.current = setTimeout(() => {
      searchLocation(newQuery);
    }, 500);
  };

  // Sélectionner une suggestion
  const handleSelectSuggestion = (suggestion) => {
    const displayName = formatDisplayName(suggestion);
    setQuery(displayName);
    setSelectedLocation({
      name: displayName,
      latitude: parseFloat(suggestion.lat),
      longitude: parseFloat(suggestion.lon)
    });
    setShowSuggestions(false);
    setSuggestions([]);
    setError('');

    // Notifier le parent
    if (onChange) onChange(displayName);
    if (onLocationSelect) {
      onLocationSelect({
        name: displayName,
        latitude: parseFloat(suggestion.lat),
        longitude: parseFloat(suggestion.lon)
      });
    }
  };

  // Formater le nom d'affichage
  const formatDisplayName = (suggestion) => {
    const address = suggestion.address;
    const parts = [];

    if (address.city) parts.push(address.city);
    else if (address.town) parts.push(address.town);
    else if (address.village) parts.push(address.village);
    else if (address.municipality) parts.push(address.municipality);

    if (address.state) parts.push(address.state);
    if (address.country) parts.push(address.country);

    return parts.join(', ') || suggestion.display_name;
  };

  return (
    <div ref={wrapperRef} className="relative">
      <label className="block text-white font-semibold mb-2 flex items-center">
        <MapPin size={20} className="mr-2 text-indigo-600" />
        Ville {required && '*'}
      </label>

      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={handleInputChange}
          className={`w-full px-4 py-2 pr-10 border bg-slate-700 text-white ${
            error ? 'border-red-500' : selectedLocation ? 'border-green-500' : 'border-gray-300'
          } rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent`}
          placeholder="Ex: Montréal, QC ou Paris, France"
          autoComplete="off"
        />

        {/* Icône de statut */}
        <div className="absolute right-3 top-3">
          {loading ? (
            <Loader size={20} className="text-gray-400 animate-spin" />
          ) : selectedLocation ? (
            <CheckCircle size={20} className="text-green-500" />
          ) : null}
        </div>
      </div>

      {/* Message d'aide */}
      <p className="text-sm text-gray-500 mt-1">
        {selectedLocation 
          ? '✓ Localisation validée'
          : 'Tapez au moins 3 caractères pour voir les suggestions'
        }
      </p>

      {/* Message d'erreur */}
      {error && (
        <p className="text-sm text-red-600 mt-1">{error}</p>
      )}

      {/* Liste des suggestions */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-10 w-full mt-1 bg-slate-700 border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleSelectSuggestion(suggestion)}
              className="w-full text-left px-4 py-3 hover:bg-indigo-50 transition border-b border-gray-100 last:border-b-0"
            >
              <div className="flex items-start">
                <MapPin size={16} className="text-indigo-600 mr-2 mt-1 flex-shrink-0" />
                <div>
                  <p className="font-semibold text-white">
                    {formatDisplayName(suggestion)}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    {suggestion.display_name}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default LocationAutocomplete;